import React, { createContext, useContext, useState } from 'react'
import Modal from '~/components/_layouts/Modal/index'
import { useMutation } from '@tanstack/react-query'
import { apiPatch } from '~/util/api'
import { PlusSign } from './icons'
import { cn } from '~/lib/utils'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table'
import ResourceTile from './ResourceTile'
import YnsIcon from '~/components/icons/YnsIcon'
import { queryClient, useCustomActionPlan } from './CustomActionPlanProvider'
import { Button } from '~/components/_atoms/index'
import Spinner from '~/components/shared/Spinner'
import { TableCell, TableRow } from '~/lib/ui/table'
import NewCoachResourceForm from './NewCoachResourceForm'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '~/lib/ui/select'
import { Input } from '~/lib/ui/input'
import { Label } from '~/lib/ui/label'
import { usePagination } from './PaginationProvider'

const ResourceLibraryContext = createContext()

export const useResourceLibrary = () => useContext(ResourceLibraryContext)

export const AddTaskModal = () => {
  const baseRootClassName = cn(
    'mt-5 w-full min-h-[10rem] rounded-[20px] border border-[#B9B9B9] border-[1px] text-center content-center justify-items-center cursor-pointer opacity-100',
  )
  const [isOpen, setIsOpen] = useState(false)
  const [showCreateResourceForm, setShowCreateResourceForm] = useState(false)
  const { candidateId } = useCustomActionPlan()

  if (!isOpen)
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={baseRootClassName}
        style={{
          borderStyle: 'dashed',
          borderImage:
            'repeating-linear-gradient(90deg, rgba(0,0,0,0.5) 0 32px, transparent 32px 64px) 30',
        }}
      >
        <div className="flex flex-row items-center justify-center">
          <PlusSign className="mr-3" />{' '}
          <span className="font-roboto text-xl font-normal">Add New Task</span>
        </div>
      </button>
    )

  const handleResourceCreated = () => {
    queryClient.invalidateQueries(['resource_library', candidateId])
    setShowCreateResourceForm(false)
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      size="md"
      title="Add a New Task"
    >
      {showCreateResourceForm ? (
        <NewCoachResourceForm
          showCreateResourceForm={setShowCreateResourceForm}
          onCancel={() => setShowCreateResourceForm(false)}
          onResourceCreated={handleResourceCreated}
          setIsOpen={setIsOpen}
        />
      ) : (
        <ResourceLibraryDataTable
          setShowCreateResourceForm={setShowCreateResourceForm}
        />
      )}
    </Modal>
  )
}

const ResourceLibraryDataTable = ({ setShowCreateResourceForm }) => {
  const { resources } = useCustomActionPlan()
  const { pagination, setPagination } = usePagination()
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState([])
  const [columnFilters, setColumnFilters] = useState([
    { id: 'task', value: 'all' },
  ])

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      pagination,
      globalFilter,
      sorting,
      columnFilters,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: 'includesString',
    enableMultiSorting: true,
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    sortingFns: {
      coachCreated: (rowA, rowB) => {
        return rowA.original.is_coach_created === rowB.original.is_coach_created
          ? 0
          : rowA.original.is_coach_created
            ? -1
            : 1
      },
    },
  })

  const handleFilterValueChange = (value) =>
    setColumnFilters([{ id: 'task', value }])

  return (
    <div className="font-roboto">
      <div className="mb-4 flex flex-col">
        <Label>Search YNS Resources</Label>
        <Input
          type="text"
          className="mb-4 mt-2 h-14"
          value={globalFilter}
          onChange={(e) => table.setGlobalFilter(String(e.target.value))}
        />
        <div className="flex flex-row items-center justify-between">
          <div className="w-56 flex-shrink-0">
            <Select
              value={columnFilters[0]?.value}
              onValueChange={handleFilterValueChange}
            >
              <SelectTrigger aria-label="Show Coach Created Only">
                <SelectValue placeholder="Show Coach Created Only" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show All Resources</SelectItem>
                <SelectItem value="coach_created">
                  Show Coach Created Only
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-1 justify-end">
            <Button
              onClick={() => setShowCreateResourceForm(true)}
              variant="link"
            >
              Create New Coach Resource
            </Button>
          </div>
        </div>
      </div>
      <h3 className="my-4 font-roboto-slab font-medium text-intoo-blue-medium">
        Results
      </h3>
      <table className="w-full">
        <tbody>
          {table.getPaginationRowModel().rows.map((row, index) => (
            <TableRow
              key={`${row.id}-table_row`}
              row={row}
              index={index}
              id={row.id}
              fred={5}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell className="!p-0" key={`${row.id}_${cell.id}`}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </table>
      {resources?.length > 10 && (
        <div className="flex items-center justify-between p-4">
          <button
            className="rounded border px-3 py-1 disabled:opacity-50"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <button
            className="rounded border px-3 py-1 disabled:opacity-50"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

const Resource = ({ row }) => {
  const { candidateId, isLoading } = useCustomActionPlan()
  const {
    name,
    completed,
    skipped_by_candidate,
    skipped_by_coach,
    description,
    length,
    type,
    resource,
    on_candidate_tracker,
  } = row.original

  const PLATFORM_NAME = 'Your Next Step'
  const replacePlatformName = (str) =>
    typeof str === 'string'
      ? str.replace(/%\{platform_name\}/g, PLATFORM_NAME)
      : str

  const tile = {
    title: replacePlatformName(name),
    description: replacePlatformName(description),
    time: length,
    type,
    completed,
    withBorder: true,
    skipped_by_candidate,
    skipped_by_coach,
  }

  const updateTrackerMutationPath = async () => {
    const response = await apiPatch(
      `/coach/candidates/${candidateId}/trackers/add_item.json?slug=${resource}`,
    )
    return response
  }

  const updateTrackerMutation = useMutation({
    mutationFn: updateTrackerMutationPath,
    onSuccess: () => {
      queryClient.invalidateQueries(['resource_library', candidateId])
    },
    onError: (error) => {
      console.error('error adding task to action plan', error)
    },
  })

  const addTaskToActionPlan = () => {
    updateTrackerMutation.mutate()
  }

  const addable = !completed && !on_candidate_tracker && !isLoading
  const alreadyAdded = on_candidate_tracker && !isLoading

  return (
    <ResourceTile {...tile}>
      {isLoading && <Spinner className="mx-auto my-4" />}
      {addable && (
        <div className="flex flex-col items-center gap-1 rounded-[10px] px-4 py-2 hover:bg-intoo-blue-light">
          <Button
            variant="link"
            onClick={addTaskToActionPlan}
            className="flex flex-col items-center !text-intoo-text"
          >
            <YnsIcon name="add" />
            <div className="text-sm">Add</div>
          </Button>
        </div>
      )}
      {alreadyAdded && (
        <div className="flex flex-col items-center gap-2 rounded-[10px] bg-intoo-blue-medium px-4 py-2 text-white">
          <YnsIcon name="completed" />
          <div className="text-sm">
            {completed ? 'Completed by Candidate' : 'Added'}
          </div>
        </div>
      )}
    </ResourceTile>
  )
}

const columns = [
  {
    accessorKey: 'task',
    accessorFn: (row) => `${row.name} ${row.description}`,
    cell: (props) => <Resource {...props} />,
    sortingFn: 'coachCreated',
    filterFn: (row, _columnId, filterValue) => {
      if (filterValue === 'all') return true
      if (filterValue === 'coach_created') {
        return !!row.original.is_coach_created
      }
    },
  },
]
